
import React, { useState } from 'react';
import { Loader2, ArrowRight, ShieldCheck } from 'lucide-react';
import { useToast } from './ui/Toaster';
import { http } from '../services/http';
import { toUserError } from '../services/errors';

interface LoginPageProps {
    onLogin: () => void;
    organizationName?: string;
}

export const LoginPage: React.FC<LoginPageProps> = ({ onLogin, organizationName }) => {
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { addToast } = useToast();
    const resolvedOrgName = String(organizationName || 'АрхивЛинк');
    const orgInitial = (resolvedOrgName.trim()[0] || 'А').toUpperCase();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        const form = e.currentTarget as HTMLFormElement;
        const fd = new FormData(form);
        const formUsername = String(fd.get('username') || username || '').trim();
        const formPassword = String(fd.get('password') || password || '');
        if (!formUsername || !formPassword) return;

        setLoading(true);
        try {
            setUsername(formUsername);
            setPassword(formPassword);
            const body = { username: formUsername, password: formPassword };
            const resp = await http.post<{ token: string; user: any }>(
              '/auth/login',
              body
            );
            try { localStorage.setItem('al_jwt', resp.token); } catch {}
            try { localStorage.setItem('al_user', JSON.stringify(resp.user)); } catch {}
            try {
              if ((resp.user as any)?.mustChangePassword) localStorage.setItem('al_must_change_password', '1');
              else localStorage.removeItem('al_must_change_password');
            } catch {}
            addToast('Успешный вход в систему', 'success');
            onLogin();
        } catch (err: any) {
            const ue = toUserError(err, 'Ошибка авторизации');
            addToast(ue.message, 'error');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 px-8">
            <div className="mx-auto w-full max-w-md text-center">
                <div className="mx-auto h-16 w-16 bg-brand-600 rounded-xl flex items-center justify-center shadow-lg transform -rotate-6">
                    <span className="text-3xl font-bold text-white">{orgInitial}</span>
                </div>
                <h2 className="mt-6 text-3xl font-extrabold text-gray-900 tracking-tight">
                    {resolvedOrgName}
                </h2>
                <p className="mt-2 text-sm text-gray-600">
                    Корпоративная платформа
                </p>
            </div>

            <div className="mt-8 mx-auto w-full max-w-md">
                <div className="bg-white py-8 px-10 shadow-xl shadow-gray-200/50 rounded-xl border border-gray-100">
                    <form className="space-y-6" onSubmit={handleSubmit}>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Логин
                            </label>
                            <div className="mt-1">
                                <input
                                    type="text"
                                    name="username"
                                    autoComplete="username"
                                    required
                                    value={username}
                                    onChange={e => setUsername(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700">
                                Пароль
                            </label>
                            <div className="mt-1">
                                <input
                                    type="password"
                                    name="password"
                                    autoComplete="current-password"
                                    required
                                    value={password}
                                    onChange={e => setPassword(e.target.value)}
                                    className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-lg shadow-sm placeholder-gray-400 focus:outline-none focus:ring-brand-500 focus:border-brand-500 text-sm"
                                />
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full flex justify-center py-2.5 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-brand-600 hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                            >
                                {loading ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <span className="flex items-center gap-2">Войти <ArrowRight className="w-4 h-4"/></span>
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-200" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-white text-gray-500 flex items-center gap-1">
                                    <ShieldCheck className="w-4 h-4"/> Безопасное соединение
                                </span>
                            </div>
                        </div>
                    </div>

                </div>
                <p className="text-center text-xs text-gray-400 mt-4">
                    &copy; 2024 {resolvedOrgName} Enterprise. Все права защищены.
                </p>
            </div>
        </div>
    );
};
